import type {
	LWApiPublicAssetNetworkPrivateVpnDetailsParamsRaw,
	LWApiPublicAssetNetworkPrivateVpnDetailsResultRaw,
} from '@liquidweb-dev/club-sauce/public/asset/network/private/vpn/index.raw';
import type { ApiErrorI } from '@liquidweb-dev/club-sauce/types/apiError';
import { useQuery, type UseQueryOptions } from '@tanstack/react-query';
import api from 'modules/queries/api';

export const connectionTypes = {
	'site-to-site': {
		connectionType: 'site-to-site',
		connectionTypeDisplay: 'Site to Site',
		connectionTypeDisplayTruncated: 'Site to Site',
	},
	'remote-to-fabric': {
		connectionType: 'remote-to-fabric',
		connectionTypeDisplay: 'Remote to Fabric (Outbound Only)',
		connectionTypeDisplayTruncated: 'Remote (Outbound)',
	},
	'fabric-to-remote': {
		connectionType: 'fabric-to-remote',
		connectionTypeDisplay: 'Fabric to Remote (Inbound Only)',
		connectionTypeDisplayTruncated: 'Remote (Inbound)',
	},
} as const;

export type ConnectionType = keyof typeof connectionTypes;
export type ConnectionTypeDisplay =
	(typeof connectionTypes)[ConnectionType]['connectionTypeDisplay'];
export type ConnectionTypeDisplayTruncated =
	(typeof connectionTypes)[ConnectionType]['connectionTypeDisplayTruncated'];

export const isConnectionType = (value: string): value is ConnectionType => {
	return Object.keys(connectionTypes).includes(value);
};

export interface GetConnectionTypeParams {
	remoteNetworkOnly: LWApiPublicAssetNetworkPrivateVpnDetailsResultRaw['remote_network_only'];
	remoteNetworks: LWApiPublicAssetNetworkPrivateVpnDetailsResultRaw['remote_networks'];
}

export interface ConnectionTypeInfo {
	connectionType: ConnectionType;
	connectionTypeDisplay: ConnectionTypeDisplay;
	connectionTypeDisplayTruncated: ConnectionTypeDisplayTruncated;
}

export interface VpnDetails
	extends LWApiPublicAssetNetworkPrivateVpnDetailsResultRaw,
		ConnectionTypeInfo {
	/** the name the user gave the VPN upon creation */
	name?: string;
	vpnType?: string;
	vpnTypeDisplay?: string;
	vpnTypePtokId?: number;
	vpnTypePtovId?: number;
	vpnTypePrice?: number;
	rateLimit?: string;
	rateLimitDisplay?: string;
	rateLimitPtokId?: number;
	rateLimitPtovId?: number;
	rateLimitPrice?: number;
	security?: string;
	securityDisplay?: string;
	securityPtokId?: number;
	securityPtovId?: number;
	securityPrice?: number;
}

export const getConnectionType = ({
	remoteNetworkOnly,
	remoteNetworks,
}: GetConnectionTypeParams): ConnectionTypeInfo => {
	if (remoteNetworkOnly) {
		return connectionTypes['fabric-to-remote'];
	}

	if (remoteNetworks && remoteNetworks.length > 0) {
		return connectionTypes['site-to-site'];
	}

	return connectionTypes['remote-to-fabric'];
};

export const supplementDetails = (
	data: LWApiPublicAssetNetworkPrivateVpnDetailsResultRaw,
): VpnDetails => {
	const supplimentedData: VpnDetails = {
		...data,
		...getConnectionType({
			remoteNetworkOnly: data.remote_network_only,
			remoteNetworks: data.remote_networks,
		}),
	};

	const vpnType = data?.asset?.featureDetails?.VpnType;
	if (vpnType) {
		supplimentedData.vpnType = vpnType.value;
		supplimentedData.vpnTypeDisplay = vpnType.description;
		supplimentedData.vpnTypePtokId = vpnType.ptok_id;
		supplimentedData.vpnTypePtovId = vpnType.ptov_id;
		supplimentedData.vpnTypePrice = Number(vpnType.price);
	}

	const rateLimit = data?.asset?.featureDetails?.BandwidthRateLimit;
	if (rateLimit) {
		supplimentedData.rateLimit = rateLimit.value;
		supplimentedData.rateLimitDisplay = rateLimit.description;
		supplimentedData.rateLimitPtokId = rateLimit.ptok_id;
		supplimentedData.rateLimitPtovId = rateLimit.ptov_id;
		supplimentedData.rateLimitPrice = Number(rateLimit.price);
	}

	const security = data?.asset?.featureDetails?.key_exchange;
	if (security) {
		supplimentedData.security = security.value;
		supplimentedData.securityDisplay = security.description;
		supplimentedData.securityPtokId = security.ptok_id;
		supplimentedData.securityPtovId = security.ptov_id;
		supplimentedData.securityPrice = Number(security.price);
	}

	const name = data?.asset?.domain;
	if (name) {
		supplimentedData.name = name;
	}

	return supplimentedData;
};

export const ASSET_NETWORK_PRIVATE_VPN_DETAILS_OUERY_KEY =
	'/asset/network/private/vpn/details';

const useAssetNetworkPrivateVpnDetails = (
	params: LWApiPublicAssetNetworkPrivateVpnDetailsParamsRaw,
	options?: UseQueryOptions<
		LWApiPublicAssetNetworkPrivateVpnDetailsResultRaw,
		ApiErrorI,
		VpnDetails
	>,
) =>
	useQuery({
		queryKey: [ASSET_NETWORK_PRIVATE_VPN_DETAILS_OUERY_KEY, params],
		queryFn: () => api.asset.network.private.vpn.apiRaw.details(params),
		select: supplementDetails,
		...options,
	});

export default useAssetNetworkPrivateVpnDetails;
